import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { AlertCircle, AlertTriangle, Info, Receipt } from 'lucide-react';
import { format, parse } from 'date-fns';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDateFnsLocale } from '@/lib/format';
import { formatMoney, minorToMajor } from '@/lib/money';
import { useCurrency } from '@/hooks/useCurrency';
import { resolveBillingModeLabel } from '@/lib/settlingInBillingLabels';
import type { SettlingInBillingPreview } from '@/types/settlingIn';

/** The shape of the preview query result this block consumes. */
export interface BillingPreviewBlockProps {
  data?: SettlingInBillingPreview;
  isLoading?: boolean;
  isError?: boolean;
  isFetching?: boolean;
  onRetry?: () => void;
  className?: string;
}

/**
 * Read-only billing preview shown in CompleteDialog and SignOffDialog (ST-6/S18).
 *
 * Billing is AUTOMATIC once completion and sign-off both exist, so whichever of
 * the two actions completes the pair is the billing-triggering action. This block
 * exists so that action is never performed blind.
 *
 * It branches on `preview_state`, NEVER on `amount === 0` — a deliberate zero
 * charge (INCLUDED/FREE, or a PER_DAY plan with no present days yet) and a
 * misconfiguration (no amount set, unresolvable mode) are different facts that
 * would otherwise both render as `CHF 0.00`.
 *
 * The figure is ADVISORY: it takes no lock and is not a reservation.
 */
export function BillingPreviewBlock({
  data,
  isLoading,
  isError,
  isFetching,
  onRetry,
  className,
}: BillingPreviewBlockProps) {
  const { i18n } = useLingui();
  const { currency: orgCurrency } = useCurrency();
  // Prefer the currency the server resolved for this plan; fall back to the org
  // config. Never a hardcoded string.
  const currency = data?.currency || orgCurrency;

  const modeLabel = useMemo(
    () => resolveBillingModeLabel(i18n, data?.billing_mode),
    [data?.billing_mode, i18n],
  );

  const targetPeriodLabel = useMemo(() => {
    if (!data?.target_period) return undefined;
    // "YYYY-MM" → a localized month + year. A day-precision date would read as
    // though the invoice were dated that day.
    const parsed = parse(data.target_period, 'yyyy-MM', new Date());
    if (Number.isNaN(parsed.getTime())) return undefined;
    return format(parsed, 'MMMM yyyy', { locale: getDateFnsLocale(i18n.locale) });
  }, [data?.target_period, i18n.locale]);

  const title = i18n._({ id: 'settlingIn.billingPreview.title', message: 'Billing preview' });

  if (isError) {
    return (
      <div
        className={cn(
          'flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive',
          className,
        )}
        role="alert"
        data-testid="billing-preview-error"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="space-y-2">
          <span>
            {i18n._({
              id: 'settlingIn.billingPreview.loadFailed',
              message:
                'The billing preview could not be loaded. This does NOT mean there is no charge — retry to see what will be billed.',
            })}
          </span>
          {onRetry && (
            <div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onRetry}
                disabled={isFetching}
              >
                {i18n._({ id: 'common.retry', message: 'Try again' })}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div
        className={cn('rounded-md border bg-muted/30 p-3 text-sm', className)}
        data-testid="billing-preview-loading"
      >
        <span className="text-muted-foreground">
          {i18n._({ id: 'settlingIn.billingPreview.loading', message: 'Loading billing preview…' })}
        </span>
      </div>
    );
  }

  const previewState = data.preview_state;
  // A subsidised contract bills its settling-in days as regular care, so no fee
  // line exists in any mode (#1703). The backend decides; the block never
  // re-derives the rule.
  const feeWaivedSubsidised = data.settling_in_fee_applies === false;
  // States where no charge can land, so a target invoice period is meaningless.
  const isAdvisoryState =
    feeWaivedSubsidised ||
    previewState === 'amount_missing' ||
    previewState === 'mode_unknown' ||
    previewState === 'zero_not_billed';

  return (
    <div
      className={cn('rounded-md border bg-card p-3 text-sm space-y-2', className)}
      data-testid="billing-preview"
    >
      <div className="flex items-center gap-2">
        <Receipt className="h-4 w-4 shrink-0 text-capacity-normal-on-surface" aria-hidden="true" />
        <span className="font-semibold">{title}</span>
      </div>

      {/* Mode is always stated, whatever the preview state. */}
      <Row
        label={i18n._({ id: 'settlingIn.billingPreview.mode', message: 'Mode' })}
        value={modeLabel ?? '—'}
      />

      {feeWaivedSubsidised && (
        <p className="text-muted-foreground" data-testid="billing-preview-no-fee-subsidised">
          {i18n._({
            id: 'settlingIn.closure.noFeeSubsidised',
            message:
              'No settling-in fee: this contract is subsidised, and the settling-in days are billed as regular care.',
          })}
        </p>
      )}

      {previewState === 'no_charge' && !feeWaivedSubsidised && (
        <p className="text-muted-foreground" data-testid="billing-preview-no-charge">
          {i18n._({
            id: 'settlingIn.billingPreview.noCharge',
            message: 'No settling-in charge (mode: {mode})',
            values: { mode: modeLabel ?? '' },
          })}
        </p>
      )}

      {previewState === 'amount_missing' && (
        <p
          className="flex items-start gap-2 text-capacity-warning-on-surface"
          role="alert"
          data-testid="billing-preview-amount-missing"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {i18n._({
              id: 'settlingIn.billingPreview.amountMissing',
              message:
                'No settling-in amount is configured for this plan (mode: {mode}) — nothing will be billed.',
              values: { mode: modeLabel ?? '' },
            })}
          </span>
        </p>
      )}

      {/* A paid mode whose computed charge is zero. Invoice generation suppresses
          `Amount <= 0` entirely, so nothing lands — rendering the money rows here
          would show a "CHF 0.00" charge that never happens. Quantity is stated so
          the cause ("0 days tracked") is visible. */}
      {previewState === 'zero_not_billed' && (
        <>
          <Row
            label={i18n._({ id: 'settlingIn.billingPreview.quantity', message: 'Quantity' })}
            value={String(data.quantity ?? 0)}
          />
          <p
            className="flex items-start gap-2 text-capacity-warning-on-surface"
            role="alert"
            data-testid="billing-preview-zero-not-billed"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {i18n._({
                id: 'settlingIn.billingPreview.zeroNotBilled',
                message:
                  'This plan currently computes to no charge (mode: {mode}), so no settling-in line will be billed.',
                values: { mode: modeLabel ?? '' },
              })}
            </span>
          </p>
        </>
      )}

      {previewState === 'mode_unknown' && (
        <p
          className="flex items-start gap-2 text-capacity-warning-on-surface"
          role="alert"
          data-testid="billing-preview-mode-unknown"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {i18n._({
              id: 'settlingIn.billingPreview.modeUnknown',
              message: 'The billing mode for this plan could not be resolved.',
            })}
          </span>
        </p>
      )}

      {previewState === 'computed' && (
        <>
          {/* Quantity × rate is rendered explicitly so a zero amount reads as
              legibly DERIVED ("0 days × rate"), not as a formatting artefact. */}
          <Row
            label={i18n._({ id: 'settlingIn.billingPreview.quantity', message: 'Quantity' })}
            value={String(data.quantity ?? 0)}
          />
          <Row
            label={i18n._({ id: 'settlingIn.billingPreview.unitRate', message: 'Unit rate' })}
            value={formatMoney(minorToMajor(data.unit_rate ?? 0), true, currency)}
          />
          <Row
            label={i18n._({ id: 'settlingIn.billingPreview.amount', message: 'Amount' })}
            value={formatMoney(minorToMajor(data.amount ?? 0), true, currency)}
            emphasise
          />
        </>
      )}

      {/* The target period is meaningful for any state that can produce a charge. */}
      {!isAdvisoryState && (
        <Row
          label={i18n._({
            id: 'settlingIn.billingPreview.targetPeriod',
            message: 'Target invoice period',
          })}
          value={
            targetPeriodLabel ??
            i18n._({ id: 'settlingIn.billingPreview.periodUnknown', message: '—' })
          }
        />
      )}

      {!targetPeriodLabel && !isAdvisoryState && (
        <p className="text-xs text-muted-foreground">
          {i18n._({
            id: 'settlingIn.billingPreview.periodHint',
            message: 'The target period resolves once a sign-off date is entered.',
          })}
        </p>
      )}

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        <span>
          {i18n._({
            id: 'settlingIn.billingPreview.staleHint',
            message:
              'This is a preview computed now. The invoice is generated later from the data at that time, so the final figure may differ.',
          })}
        </span>
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  emphasise,
}: {
  label: string;
  value: string;
  emphasise?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn(emphasise && 'font-semibold')}>{value}</span>
    </div>
  );
}
